import React, { useState } from 'react';
import { availableActions, ListingAction, toStatusAction } from '../domain/listingLifecycle';
import { isDomainError } from '../domain/errors';
import type { Listing } from '../domain/listings';
import { useRepositories } from '../hooks/useRepositories';
import { ActionError } from './PageStates';
import ScheduleField from './ScheduleField';
import { Button } from '@/components/ui/button';

const LABEL: Record<ListingAction, string> = {
  publishNow: 'Publish now',
  schedule: 'Schedule',
  changeSchedule: 'Change time',
  clearSchedule: 'Clear schedule',
  markSold: 'Mark sold',
  markForSale: 'Mark for sale again',
  withdraw: 'Withdraw',
  republish: 'Republish',
  backToDraft: 'Back to draft',
};

const NEEDS_TIME: ListingAction[] = ['schedule', 'changeSchedule'];

/**
 * The author's lifecycle actions for a listing, as its state allows
 * (016 research C1, C16). Each is shown as done only once the server
 * answers; a refusal leaves the listing as it was.
 */
export default function ListingLifecycleMenu({
  listing,
  onChanged,
  onGone,
}: {
  listing: Listing;
  onChanged: (listing: Listing) => void;
  /** The server no longer shows me the listing (deleted elsewhere, say). */
  onGone?: () => void;
}) {
  const { listings } = useRepositories();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [scheduling, setScheduling] = useState(false);
  const [publishAt, setPublishAt] = useState<string | null>(listing.publishAt);
  const actions = availableActions(listing);
  if (actions.length === 0) return null;

  const run = async (action: ListingAction, at?: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const changed = await listings.changeStatus(listing.id, toStatusAction(action), at);
      setScheduling(false);
      onChanged(changed);
    } catch (e) {
      if (onGone && isDomainError(e, 'notFound')) onGone();
      else setError(e);
    } finally {
      setBusy(false);
    }
  };

  const choose = (action: ListingAction) => {
    if (NEEDS_TIME.includes(action)) setScheduling(true);
    else run(action);
  };

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Listing status" className="flex flex-wrap gap-2">
        {actions.map((action) => (
          <Button
            key={action}
            variant={action === 'publishNow' ? 'default' : 'secondary'}
            size="sm"
            className="rounded-full"
            disabled={busy}
            onClick={() => choose(action)}
          >
            {LABEL[action]}
          </Button>
        ))}
      </div>
      {scheduling && (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl bg-muted p-4">
          <div className="min-w-56 flex-1">
            <ScheduleField
              id={`schedule-${listing.id}`}
              value={publishAt}
              onChange={setPublishAt}
            />
          </div>
          <Button
            size="sm"
            className="rounded-full"
            disabled={busy || !publishAt}
            onClick={() => run('schedule', publishAt)}
          >
            Confirm time
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full"
            onClick={() => setScheduling(false)}
          >
            Cancel
          </Button>
        </div>
      )}
      {error !== null && <ActionError error={error} />}
    </div>
  );
}
